
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart2, Calendar, MapPin, User, Users, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import AdminOverview from '@/components/admin/AdminOverview';
import TechnicianList from '@/components/admin/TechnicianList';
import ZonesList from '@/components/admin/ZonesList';
import SecureAdminAccess from '@/components/guards/SecureAdminAccess';

const AdminPanel: React.FC = () => {
  const { profile } = useAuth();
  const { isAdmin, adminProfile, isLoading, logAdminActivity } = useAdminAuth();
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = React.useState(false);

  React.useEffect(() => {
    if (isAdmin || hasAccess) {
      logAdminActivity('admin_panel_accessed', { 
        userProfile: profile?.name || 'Unknown',
        timestamp: new Date().toISOString()
      });
    }
  }, [isAdmin, hasAccess, logAdminActivity, profile]);

  if (isLoading) {
    return (
      <div className="container py-8 animate-fade-in">
        <div className="flex justify-center items-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!isAdmin && !hasAccess) {
    return (
      <SecureAdminAccess setIsAdmin={setHasAccess}>
        <AdminPanelContent 
          adminProfile={adminProfile} 
          navigate={navigate}
          logAdminActivity={logAdminActivity}
        />
      </SecureAdminAccess>
    );
  }
  
  return (
    <AdminPanelContent 
      adminProfile={adminProfile} 
      navigate={navigate}
      logAdminActivity={logAdminActivity}
    />
  );
};

interface AdminPanelContentProps {
  adminProfile: any;
  navigate: (path: string) => void;
  logAdminActivity: (action: string, details?: any) => void;
}

const AdminPanelContent: React.FC<AdminPanelContentProps> = ({ 
  adminProfile, 
  navigate, 
  logAdminActivity 
}) => {
  return (
    <div className="container py-8 animate-fade-in">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2 text-gradient flex items-center gap-2">
              <Shield className="h-8 w-8" />
              Admin Panel
            </h1>
            <p className="text-muted-foreground">
              Manage your plant care business
              {adminProfile && (
                <span className="ml-2 text-primary font-medium">
                  ({adminProfile.adminLevel})
                </span>
              )}
            </p>
          </div>
        </div>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Technicians
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">4</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Customers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">27</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Visits This Month
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">42</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Service Zones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">3</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="technicians">Technicians</TabsTrigger>
            <TabsTrigger value="zones">Service Zones</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="animate-fade-in">
            <AdminOverview />
          </TabsContent>
          
          <TabsContent value="technicians" className="animate-fade-in">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-medium">Technician Management</h3>
              <Button 
                onClick={() => {
                  logAdminActivity('navigate_to_technician_management');
                  navigate('/admin/technicians');
                }}
              >
                Manage Technicians
              </Button>
            </div>
            <TechnicianList />
          </TabsContent>
          
          <TabsContent value="zones" className="animate-fade-in">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-medium">Service Zone Management</h3>
              <Button 
                onClick={() => {
                  logAdminActivity('navigate_to_zone_management');
                  navigate('/admin/zones');
                }}
              >
                Manage Zones
              </Button>
            </div>
            <ZonesList />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
